/**
 * +tutorial: the guided first run (docs/design/tutorial.md). One open yard, laid out in lesson order
 * from the gate eastward:
 *
 *   gate (0,0) → move marker → Tutorial Dummy (no melee, resets when left alone) → Sparring Golem
 *   (slow, telegraphed frontal + a ground ring: the Dodge lesson) → lesson pack → graduation pack.
 *
 * NOT part of the main content bundle (no dungeon list, harness or rating sees it): the web client
 * builds its registry with `tutorialBundle(base)`. Ids live here, not in ids.ts, for the same reason.
 * The packs reuse the real trash (Fang Reaver, Serpent-Cult Adept, Spitting Serpent) unchanged.
 */
import type { ContentBundle, DungeonDef, EnemyAbility, NpcDef } from '@keystone/sim';
import { NPC } from '../ids';
import { npcMelee } from '../balance';
import { enemy, hit, telegraph } from '../npcs/common';
import { cluster } from './geometry';

export const TUTORIAL = {
  dungeon: 'tutorial_grounds',
  dummy: 'tutorial_dummy',
  golem: 'sparring_golem',
  sweepingSlam: 'npc.golem.sweeping_slam',
  shatterGround: 'npc.golem.shatter_ground',
  packs: { dummy: 'tut_dummy', golem: 'tut_golem', lesson: 'tut_pack1', graduation: 'tut_pack2' },
  /** The first "walk here" marker. */
  moveSpot: { x: 14, y: 0 },
} as const;

const SLAM_CONE = { type: 'cone' as const, range: 13, angle: (5 * Math.PI) / 9 };

/**
 * Slow frontal: 1.5 s cast, then 2 s of orange cone on the ground. Aimed at a non-tank (the human
 * when they fight it alone, whatever their role): a bot tank side-stepping it every cast would walk
 * the golem into the next pack.
 */
const sweepingSlam: EnemyAbility = enemy({
  id: TUTORIAL.sweepingSlam,
  name: 'Sweeping Slam',
  school: 'physical',
  kind: 'frontal',
  description: 'A slow, telegraphed cone at its target. Step or Dodge (Space) out of the orange.',
  cast: { type: 'cast', timeMs: 1500 },
  range: 8,
  timer: { first: 3500, every: 9000, jitter: 500 },
  target: 'randomNotTank',
  shape: SLAM_CONE,
  lockFacing: true,
  counter: ['dodge'],
  warning: { text: 'Dodge the orange!', severity: 'info' },
  effects: [telegraph(SLAM_CONE, 'caster', 2000, [hit('physical', 7000, { avoidable: false })])],
});

/** An orange ring under a non-tank (or anyone, when only one player fights it). */
const shatterGround: EnemyAbility = enemy({
  id: TUTORIAL.shatterGround,
  name: 'Shatter Ground',
  school: 'physical',
  kind: 'groundAoE',
  description: 'An orange ring under a player. It erupts after 3 s.',
  cast: { type: 'cast', timeMs: 500 },
  timer: { first: 7500, every: 11000, jitter: 1000 },
  target: 'randomNotTank',
  shape: { type: 'circle', radius: 5 },
  counter: ['dodge'],
  effects: [telegraph({ type: 'circle', radius: 5 }, 'target', 3000, [hit('physical', 6000, { avoidable: false })])],
});

export const TUTORIAL_ABILITIES: EnemyAbility[] = [sweepingSlam, shatterGround];

export const TUTORIAL_NPCS: NpcDef[] = [
  {
    // Like the Training Dummy, but it never swings back and resets once you walk away.
    id: TUTORIAL.dummy,
    name: 'Tutorial Dummy',
    tier: 'boss',
    hpMult: 1.1,
    forces: 0,
    abilities: [],
    radius: 1.2,
    combatReach: 2,
    moveSpeed: 0,
    ccImmune: true,
    idleResetMs: 6000,
    color: '#95a5a6',
  },
  {
    id: TUTORIAL.golem,
    name: 'Sparring Golem',
    tier: 'standard',
    // Lasts through the healer's lessons (tank bot + healer); DPS flows get the party's help after the Dodge.
    hpMult: 3,
    forces: 0,
    melee: npcMelee(350),
    abilities: [TUTORIAL.sweepingSlam, TUTORIAL.shatterGround],
    radius: 1.6,
    combatReach: 2.5,
    moveSpeed: 6,
    tauntDr: false,
    color: '#b08d57',
  },
];

const room = { points: [{ x: -20, y: -32 }, { x: 170, y: -32 }, { x: 170, y: 32 }, { x: -20, y: 32 }] };
const entrance = { x: 0, y: 0 };

export const tutorialGrounds: DungeonDef = {
  id: TUTORIAL.dungeon,
  name: 'Tutorial Yard',
  timerMs: 20 * 60 * 1000,
  forcesRequired: 48,
  entrance,
  entranceFacing: 0,
  rooms: [{ id: 'yard', name: 'Training Yard', shape: room }],
  corridors: [],
  packs: [
    { id: TUTORIAL.packs.dummy, roomId: 'yard', aggroRadius: 0, spawns: [{ npcId: TUTORIAL.dummy, pos: { x: 26, y: 9 }, facing: Math.PI }] },
    { id: TUTORIAL.packs.golem, roomId: 'yard', aggroRadius: 0, spawns: [{ npcId: TUTORIAL.golem, pos: { x: 46, y: -12 }, facing: Math.PI }] },
    {
      id: TUTORIAL.packs.lesson,
      roomId: 'yard',
      aggroRadius: 8,
      socialRadius: 12,
      spawns: cluster({ x: 96, y: 6 }, [NPC.fangReaver, NPC.cultAdept, NPC.spittingSerpent, NPC.fangReaver], entrance),
    },
    {
      id: TUTORIAL.packs.graduation,
      roomId: 'yard',
      aggroRadius: 8,
      socialRadius: 12,
      spawns: cluster({ x: 142, y: -4 }, [NPC.fangReaver, NPC.cultAdept, NPC.spittingSerpent, NPC.fangReaver], entrance),
    },
  ],
  route: [TUTORIAL.packs.lesson, TUTORIAL.packs.graduation],
  requiredEncounters: [],
  bounds: { x: -20, y: -32, w: 190, h: 64 },
};

/** The base bundle plus the tutorial yard, its dummy, golem and golem abilities. */
export function tutorialBundle(base: ContentBundle): ContentBundle {
  return {
    ...base,
    npcs: [...base.npcs.filter((n) => !TUTORIAL_NPCS.some((t) => t.id === n.id)), ...TUTORIAL_NPCS],
    enemyAbilities: [...base.enemyAbilities.filter((a) => !TUTORIAL_ABILITIES.some((t) => t.id === a.id)), ...TUTORIAL_ABILITIES],
    dungeons: [...base.dungeons.filter((d) => d.id !== TUTORIAL.dungeon), tutorialGrounds],
  };
}
